import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "reactstrap";
import { FaTimes } from "react-icons/fa";

import { getRecentMediaErrors, getMediaErrorCount, CATEGORY_REASON_KEY, ErrorCategory } from "../../lib/MediaErrorLog";
import { getUserNicknameBySeq, formatCodecInfo } from "../../lib/MediaSoupClient";

// 20260904 handsup(학생 개별 영상)과 smallGroup(모둠토론)은 둘 다 "학생" 쪽 표시를 쓴다.
const isStudentRoom = roomKind => roomKind === "handsup" || roomKind === "smallGroup";

const getTargetKey = (roomKind, mediaKind) => {
    if (isStudentRoom(roomKind)) {
        if (mediaKind === "audio") return "역할_학생_음성";
        if (mediaKind === "mic") return "역할_학생_마이크";
        return "역할_학생_영상";
    }
    return mediaKind === "audio" ? "역할_선생님_음성" : "역할_선생님_영상";
};

const formatTime = date => {
    if (!(date instanceof Date)) return "";
    const pad = value => String(value).padStart(2, "0");
    return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

/**
 * 재생 실패/통신 오류 누적 로그를 최근 10건까지 보여주는 패널.
 * "대상 - 원인 (시각)" 형식으로 한 줄씩 표시하며, 목록 영역은 그 이상일 때 스크롤된다.
 * "전체 보기" 토글로 누적된 전체 기록도 확인할 수 있다.
 *
 * @since 2026-09-04.
 */
const MediaErrorLogPanel = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const [showAll, setShowAll] = useState(false); // 20260904 전체 보기 토글 추가 by claude
    const totalCount = getMediaErrorCount();
    const entries = getRecentMediaErrors(showAll ? totalCount : 10);

    const getTargetLabel = entry => {
        const key = getTargetKey(entry.roomKind, entry.mediaKind);
        if (isStudentRoom(entry.roomKind)) {
            const nickname = getUserNicknameBySeq(entry.prodUserSeq) || t("사용자_번호", { ns: "live", seq: entry.prodUserSeq });
            return t(key, { ns: "live", name: nickname });
        }
        return t(key, { ns: "live" });
    };

    const getReasonLabel = entry => {
        const key = CATEGORY_REASON_KEY[entry.category] || CATEGORY_REASON_KEY[ErrorCategory.UNKNOWN];
        if (entry.category === ErrorCategory.CODEC) {
            const codecInfo = Array.isArray(entry.producerCodecs) && entry.producerCodecs.length > 0
                ? formatCodecInfo(entry.producerCodecs[0])
                : null;
            return t(key, { ns: "live", codec: codecInfo ? codecInfo.codecName : t("알_수_없음", { ns: "live" }) });
        }
        return t(key, { ns: "live" });
    };

    // 20260904 부트스트랩 card 기본 스타일 대신 hc-session-alert-card로 교체해 라이트/다크(soft-dark)
    // 테마에 맞춰 배경·글자색이 자동으로 바뀌도록 한다. 헤더/목록은 카드의 색상을 그대로 상속받는다. by claude
    return (
        <Modal isOpen={isOpen} toggle={onClose} centered className="media-error-log-modal" contentClassName="hc-session-alert-content">
            <div className="hc-session-alert-card">
                <div className="media-error-log-header">
                    <span className="media-error-log-title">{t("오류_로그_제목", { ns: "live", count: totalCount })}</span>
                    <button type="button" className="media-error-log-close" aria-label={t("확인", { ns: "common" })} onClick={onClose}>
                        <FaTimes aria-hidden="true" />
                    </button>
                </div>
                {
                    entries.length === 0 ?
                        <p className="media-error-log-empty mb-0">{t("오류_로그_없음", { ns: "live" })}</p> :
                        <div className="media-error-log-list">
                            {
                                entries.map(entry => (
                                    <div key={entry.id} className="media-error-log-row">
                                        <span className={`media-error-log-dot media-error-log-dot-${entry.category}`} />
                                        <span className="media-error-log-text">
                                            {t("오류_로그_항목", {
                                                ns: "live",
                                                target: getTargetLabel(entry),
                                                reason: getReasonLabel(entry),
                                                time: formatTime(entry.time)
                                            })}
                                        </span>
                                    </div>
                                ))
                            }
                        </div>
                }
                {
                    // 20260904 누적 건수가 10건을 넘을 때만 전체 보기 토글을 노출한다. by claude
                    totalCount > 10 &&
                    <button type="button" className="hc-session-alert-btn hc-session-alert-btn-secondary media-error-log-toggle" onClick={() => setShowAll(value => !value)}>
                        {showAll ? t("최근_10건만_보기", { ns: "live" }) : t("전체_보기", { ns: "live" })}
                    </button>
                }
            </div>
        </Modal>
    );
};

export default MediaErrorLogPanel;
